import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import { IGeofence } from "@app/interfaces/IGeofences";
import { IVehicleQueryOptions } from "@app/interfaces/IVehicleQueryOptions";
import { DynamicReportService } from "@app/main/components/reports/_services/dynamicReports.service";
import { IGroup } from "@app/main/components/resourceManagement/groups/_models/IGroup";
import { DateService } from "@app/services/date.service";
import { GroupService } from "@app/services/groups/group.service";
import { DateRange } from "@enums/DateRange.enum";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IDriver } from "@interfaces/IDriver";
import { IVehicle } from "@interfaces/IVehicle";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TimeFilterType } from "@reports/enums/TimeFilterType";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { SessionService } from "@services/session.service";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { SettingsName } from "src/app/class/SettingsName";
import { IColumnHeader } from "../../models/IColumnHeader";
import { ReportInternationalisationOptionsComponent } from "../report-internationalisation-options/report-internationalisation-options.component";
import { ReportParametersComponent } from "../report-parameters/report-parameters.component";
import { IRowDataSourceChange } from "../row-data-source-selector2/row-data-source-selector2.component";
import "./report-filters.component.scss";

@UntilDestroy()
@Component({
    selector: "report-filters",
    template: require("./report-filters.component.html")
})
export class ReportFiltersComponent implements OnChanges {

    @Input() report: IReportDefinition;
    @Output() reportChange = new EventEmitter<IReportDefinition>();

    @Input() reportId: number;
    @Input() hideDate: boolean;

    @ViewChild("reportParameters") reportParameters: ReportParametersComponent;
    @ViewChild("reportInternationalisationOptions") reportInternationalisationOptions: ReportInternationalisationOptionsComponent;

    vehicles: IDriver[] = [];
    selectedVehicleId: string;

    drivers: IDriver[] = [];
    selectedDriverId: string;

    groups: IGroup[] = [];
    selectedGroupId: number | null = null;

    // ---

    reportChangeDebounce$ = new Subject<IReportDefinition>();

    TimeFilterType = TimeFilterType;

    startDateRange = DateRange.Today;

    loading = false;
    userTZMatch = true;
    timeZone: string;

    showParameterOptions: boolean;
    showTemperatureChartCheckBox: boolean;
    showGeofenceFilters: boolean;

    requireSingleResource: boolean;
    vehicleQueryOptions: IVehicleQueryOptions;

    maxDays: number = 92;
    isValid: boolean = true;

    CalendarDirection = CalendarDirection;

    constructor(
        private readonly dateService: DateService,
        private readonly dynamicReportService: DynamicReportService,
        private readonly sessionService: SessionService,
        private groupService: GroupService
    ) {
        this.setupChangeDebounce();
    }

    get selectedResource(): boolean {
        return !!this.report.Parameters.VehicleId || !!this.report.Parameters.DriverId;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['report']) {
            this.showParameterOptions = this.dynamicReportService.getCustomParameterDefinitions(this.report).length > 0;
            this.showGeofenceFilters = this.dynamicReportService.shouldShowGeofenceFilters(this.report);
            this.checkShouldHideDateFilter();
            this.checkForTemperatureChartColumns();
            this.loadGroups();
        }

        this.resourceChecks();
    }

    ngOnInit() {
        this.createTimezoneOffsetWarning();
        if (!this.report.Parameters.VehicleGroupId) {
            this.report.Parameters.VehicleGroupId = null;
        }
        this.selectedGroupId = this.report.Parameters.VehicleGroupId;
    }

    loadGroups() {
        this.groupService.getGroups().then((groups) => {
            this.groups = groups;
            this.checkAndUpdateGroupId();
        });
    }

    checkAndUpdateGroupId() {
        if (!this.selectedGroupId) return;

        const matchingGroup = this.groups.find((group) => group.Id === this.selectedGroupId);
        if (!matchingGroup) {
            this.selectedGroupId = null;
            this.report.Parameters.VehicleGroupId = null;
            this.report.Parameters.DriverGroupId = null;
        }
    }

    onReportParametersChanged(): void {
        this.triggerModelChange();
    }

    onReportI18OptionsUpdated(report: IReportDefinition): void {
        this.reportParameters?.processI18nOptionsChange();
        this.triggerModelChange();
    }

    onDateChanged(date: Date) {
        this.report.Parameters.Start = date;

        this.triggerModelChange();
    }

    onDateRangeChanged(dates: [Date, Date]) {
        if (!this.datesAreValid(dates)) {
            this.isValid = false;
            return;
        }

        this.isValid = true;

        this.report.Parameters.Start = dates[0];
        this.report.Parameters.End = dates[1];

        this.triggerModelChange();
    }

    onVehicleSelected(vehicle: IVehicle) {

        const vehicleId = vehicle?.Id;
        const vehicleIdChanged = vehicleId !== this.report.Parameters.VehicleId;

        this.report.Parameters.VehicleId = vehicleId;

        if (vehicleIdChanged) {
            this.reportParameters?.processVehicleChange();
        }

        this.triggerModelChange();
    }

    onVehicleCleared(): void {
        this.report.Parameters.VehicleId = null;

        this.reportParameters?.processVehicleChange();

        this.triggerModelChange();
    }

    onVehicleGroupSelected(group: IGroup | null) {
        if (group) {
            this.selectedGroupId = group.Id;
            this.report.Parameters.VehicleGroupId = group.Id;
        } else {
            this.selectedGroupId = null;
            this.report.Parameters.VehicleGroupId = null;
        }

        this.triggerModelChange();
    }

    onVehicleGroupCleared() {
        this.report.Parameters.VehicleGroupId = null;

        this.triggerModelChange();
    }

    onDriverSelected(driver: IDriver) {
        this.report.Parameters.DriverId = driver.Id;

        this.triggerModelChange();
    }

    onDriverCleared(): void {
        this.report.Parameters.DriverId = null;

        this.triggerModelChange();
    }

    onDriverGroupSelected(group: IGroup | null) {
        if (group) {
            this.selectedGroupId = group.Id;
            this.report.Parameters.DriverGroupId = group.Id;
        } else {
            this.selectedGroupId = null;
            this.report.Parameters.DriverGroupId = null;
        }

        this.triggerModelChange();
    }

    onDriverGroupCleared() {
        this.report.Parameters.DriverGroupId = null;

        this.triggerModelChange();
    }

    onGeofenceSelected(geofence: IGeofence): void {
        this.report.Parameters.GeofenceId = geofence.Id;

        this.triggerModelChange();
    }

    onGeofenceCleared(): void {
        this.report.Parameters.GeofenceId = null;

        this.triggerModelChange();
    }

    onSelectedTagsChanged(tagIds: number[]): void {
        this.report.Parameters.TagIds = tagIds;
    }

    onRowDataSourceChanged(change: IRowDataSourceChange): void {
        if (!this.report) {
            return;
        }

        const rowDataSourceChanged = this.report.RowDataSource !== change.row;

        this.report.RowDataSource = change.row;
        this.report.SubRowDataSource = change.subRow;

        if (!rowDataSourceChanged) {
            return;
        }

        let rowTitleHeader: IColumnHeader;

        this.dynamicReportService.getDisplayColumnHeaders().then(columnHeaders => {
            rowTitleHeader = this.dynamicReportService.findRowTitleColumn(columnHeaders);
            this.dynamicReportService.setReportDefinitionRowTitle(this.report, rowTitleHeader);
        });


        this.updateSortedHeader();
        this.triggerModelChange();
    }

    // ---

    private datesAreValid(dates: [Date, Date]): boolean {
        if (dates.some(date => isNaN(date?.getTime()))) {
            return false;
        }

        const diff = this.dateService.getDaysDifference(dates[0], dates[1]);
        if (diff > this.maxDays) {
            return false;
        }

        return true;
    }

    private triggerModelChange(): void {
        this.reportChangeDebounce$.next(this.report);
    }

    private setupChangeDebounce(): void {
        this.reportChangeDebounce$.pipe(debounceTime(50), untilDestroyed(this)).subscribe((report) => {
            this.reportChange.emit(report);
        });
    }

    private createTimezoneOffsetWarning() {
        this.dynamicReportService.isMachineAndUserTZSame().then(result => {
            this.userTZMatch = result;

            if (!this.userTZMatch) {

                let userOptions = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
                this.timeZone = userOptions.Timezone.Value;
            }
        });
    }

    private checkForTemperatureChartColumns(): void {
        this.showTemperatureChartCheckBox = this.dynamicReportService.findColumn("ProbeTemperatures", "Temperature", this.report.TableDefinition.Headers) != null;
    }

    private updateSortedHeader(): void {
        if (!this.report?.TableDefinition?.Headers?.length) {
            return;
        }

        const sortSettings = this.dynamicReportService.getSortSettings(this.report);

        this.report.TableDefinition.Headers.forEach(header => {
            header.Sorted = header.Id === sortSettings.headerId;

            if (header.Sorted) {
                header.SortAscending = sortSettings.ascending;
            }
        });
    }

    private checkShouldHideDateFilter(): void {
        if (this.hideDate) {
            return;
        }

        this.hideDate = !this.dynamicReportService.requiresDateSelector(this.report);
    }

    private resourceChecks(): void {
        this.requireSingleResource = this.dynamicReportService.restrictReportToSingleResource(this.report);

        if (this.dynamicReportService.isTemperatureReport(this.report)) {
            this.vehicleQueryOptions = {
                HasTemperatureProbeInstalled: true
            }
        }
    }
}
